import {
  Bot,
  ChevronsUp,
  CircleDashed,
  Equal,
  Inbox,
  MessageCircleQuestion,
  SignalHigh,
  SignalLow,
  UserRoundX,
  Users,
} from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import type { TaskSummary } from "@/api/generated/TaskSummary"
import type { TaskPriority } from "@/api/queries/tasks"
import { StatusBadge, type StatusTone } from "@/components/app/status-badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

export const STATUS_TONE: Record<string, StatusTone> = {
  backlog: "neutral",
  todo: "accent",
  progress: "info",
  waiting: "warning",
  review: "warning",
  blocked: "danger",
  done: "success",
  failed: "danger",
  cancelled: "neutral",
}

/** Dot colour per lane (board column headers, status pickers). */
export const STATUS_DOT: Record<string, string> = {
  backlog: "bg-muted-foreground/50",
  todo: "bg-primary",
  blocked: "bg-destructive",
  progress: "bg-info",
  review: "bg-warning",
  done: "bg-success",
}

/** Translated labels for task enums (status, priority, closure, sort order). */
export function useTaskLabels() {
  const { t } = useTranslation()
  return {
    status: (s: string) => t(`tasks.task.status.${s}` as "tasks.task.status.todo", s),
    priority: (p: string) => t(`tasks.task.priority.${p}` as "tasks.task.priority.normal", p),
    closure: (c: string) => t(`tasks.task.closure.${c}` as "tasks.task.closure.success", c),
    order: (o: string) => t(`tasks.task.order.${o}` as "tasks.task.order.sort_order", o),
  }
}

/** `display_status` badge; pulses while an agent works on the task. */
export function TaskStatusBadge({
  task,
  className,
}: {
  task: Pick<TaskSummary, "display_status" | "is_executing">
  className?: string
}) {
  const labels = useTaskLabels()
  const s = task.display_status
  return (
    <StatusBadge tone={STATUS_TONE[s] ?? "neutral"} dot pulse={task.is_executing} className={className}>
      {s === "waiting" && <MessageCircleQuestion />}
      {labels.status(s)}
    </StatusBadge>
  )
}

const PRIORITY_ICON: Record<TaskPriority, React.ComponentType<{ className?: string }>> = {
  urgent: ChevronsUp,
  high: SignalHigh,
  normal: Equal,
  low: SignalLow,
}

const PRIORITY_CLASS: Record<TaskPriority, string> = {
  urgent: "text-destructive",
  high: "text-warning",
  normal: "text-muted-foreground",
  low: "text-muted-foreground/70",
}

export function PriorityIcon({ priority, className }: { priority: string; className?: string }) {
  const p = (priority in PRIORITY_ICON ? priority : "normal") as TaskPriority
  const Icon = PRIORITY_ICON[p]
  return <Icon className={cn("size-3.5 shrink-0", PRIORITY_CLASS[p], className)} aria-hidden />
}

/** Priority icon + label. */
export function PriorityLabel({ priority, className }: { priority: string; className?: string }) {
  const labels = useTaskLabels()
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap", className)}>
      <PriorityIcon priority={priority} />
      {labels.priority(priority)}
    </span>
  )
}

/** Priority as an icon with tooltip (compact board cards). Normal priority renders nothing. */
export function PriorityFlag({ priority }: { priority: string }) {
  const labels = useTaskLabels()
  if (priority === "normal") return null
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>
        <PriorityIcon priority={priority} />
        <span className="sr-only">{labels.priority(priority)}</span>
      </TooltipTrigger>
      <TooltipContent>{labels.priority(priority)}</TooltipContent>
    </Tooltip>
  )
}

const ASSIGNEE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  agent: Bot,
  team: Users,
  pool: Inbox,
}

/** Who works on the task (agent, team, pool) or "Unassigned". */
export function AssigneeLabel({
  task,
  className,
  compact = false,
}: {
  task: Pick<TaskSummary, "assignee_kind" | "assignee_name">
  className?: string
  compact?: boolean
}) {
  const { t } = useTranslation()
  const kind = task.assignee_kind
  const Icon = (kind && ASSIGNEE_ICON[kind]) || UserRoundX
  const name = !kind
    ? t("tasks.task.assignee.unassigned")
    : kind === "pool"
      ? t("tasks.task.assignee.pool")
      : task.assignee_name || "—"
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5",
        !kind && "text-muted-foreground",
        compact && "text-xs",
        className,
      )}
    >
      <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{name}</span>
    </span>
  )
}

/** Project task number (`#12`), or a dashed marker for standalone tasks. */
export function TaskNumber({ task, className }: { task: Pick<TaskSummary, "number">; className?: string }) {
  return (
    <span className={cn("text-muted-foreground font-mono text-xs tabular-nums", className)}>
      #{task.number}
    </span>
  )
}

export function StandaloneIcon({ className }: { className?: string }) {
  return <CircleDashed className={cn("text-muted-foreground size-3.5", className)} aria-hidden />
}
